import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { logError } from "@/lib/logError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS_OPTIONS = ["Soccer", "Golf", "Tennis", "Swimming", "Volleyball", "Basketball", "Track & Field", "Other"];

const EMPTY = { name: "", email: "", phone: "", country: "", sport: "", graduation_year: "", message: "" };

const CONSENT_COPY = {
  en: { label: "I agree to the processing of my personal data per the", required: "Please confirm consent before submitting.", link: "Privacy Policy" },
  de: { label: "Ich stimme der Verarbeitung meiner personenbezogenen Daten gemäß der", required: "Bitte bestätige deine Einwilligung vor dem Absenden.", link: "Datenschutzerklärung" },
  pt: { label: "Aceito o tratamento dos meus dados pessoais nos termos da", required: "Por favor, confirma o consentimento antes de enviar.", link: "Política de Privacidade" },
};

const Input = ({ label, labelClass = "mt-6", name, value, onChange, type = "text", required = false, testId }) => (
  <div>
    <label className={`overline block ${labelClass}`}>{label}</label>
    <input type={type} name={name} value={value} onChange={onChange} required={required} data-testid={testId} className="nc-input" />
  </div>
);

const ConsentRow = ({ lang, copy, consent, onConsent }) => (
  <div className="md:col-span-2 mt-8 flex items-start gap-3">
    <input type="checkbox" id="contact-consent" data-testid="contact-consent" checked={consent} onChange={(e) => onConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-[#0F1B2D] shrink-0" />
    <label htmlFor="contact-consent" className="text-[#5E6470] text-sm leading-relaxed">
      {copy.label}{" "}
      <a href={`/${lang}/legal/privacy`} className="text-[#0F1B2D] underline underline-offset-2 hover:text-[#A38755]">{copy.link}</a>.
    </label>
  </div>
);

export const ContactForm = ({ t, lang, onSuccess }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const copy = CONSENT_COPY[lang] || CONSENT_COPY.en;
  const onFieldChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const field = (name) => ({ name, value: form[name], onChange: onFieldChange });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error(t.contact.error);
    if (!consent) return toast.error(copy.required);
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, { ...form, language: lang });
      toast.success(t.contact.success);
      setForm(EMPTY);
      onSuccess();
    } catch (err) {
      logError("Contact.submit", err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
      <Input label={t.contact.name} labelClass="mt-2" {...field("name")} required testId="contact-name" />
      <Input label={t.contact.email} labelClass="mt-2" type="email" {...field("email")} required testId="contact-email" />
      <Input label={t.contact.phone} {...field("phone")} testId="contact-phone" />
      <Input label={t.contact.country} {...field("country")} testId="contact-country" />
      <div>
        <label className="overline block mt-6">{t.contact.sport}</label>
        <select name="sport" value={form.sport} onChange={onFieldChange} data-testid="contact-sport" className="nc-input">
          <option value="">—</option>
          {SPORTS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <Input label={t.contact.year} {...field("graduation_year")} testId="contact-year" />
      <div className="md:col-span-2">
        <label className="overline block mt-6">{t.contact.message}</label>
        <textarea rows={4} name="message" value={form.message} onChange={onFieldChange} data-testid="contact-message" className="nc-input resize-none" />
      </div>
      <ConsentRow lang={lang} copy={copy} consent={consent} onConsent={setConsent} />
      <div className="md:col-span-2 mt-6">
        <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-3 px-10 py-4 bg-[#0F1B2D] text-[#C4A46A] text-[12px] tracking-[0.24em] uppercase font-bold hover:bg-[#0a1322] disabled:opacity-60">
          {submitting ? t.common.submitting : t.contact.submit}
        </button>
      </div>
    </form>
  );
};
